import styles from "./mapping.module.css";
import { isApplicable, rowStatus, type GroupDto, type RowDto } from "./types";

export const MAPPING_TABS = [
  { id: "all", label: "All" },
  { id: "unresolved", label: "Unresolved" },
  { id: "mandatory", label: "Mandatory" },
  { id: "derived", label: "Derived" },
] as const;

export type MappingTabId = (typeof MAPPING_TABS)[number]["id"];

export function readTab(value: string | null): MappingTabId {
  const found = MAPPING_TABS.find((tab) => tab.id === value);
  return found ? found.id : "all";
}

function keepRow(row: RowDto, tab: MappingTabId): boolean {
  if (tab === "unresolved") return rowStatus(row) === "unmapped" || rowStatus(row) === "error";
  if (tab === "mandatory") return isApplicable(row);
  if (tab === "derived") return row.derived;
  return true;
}

/** Client-side filter over the fetched tree; a group with no surviving row or descendant goes. */
export function filterGroups(groups: GroupDto[], tab: MappingTabId): GroupDto[] {
  if (tab === "all") return groups;
  const kept: GroupDto[] = [];
  for (const group of groups) {
    const rows = group.rows.filter((row) => keepRow(row, tab));
    const children = filterGroups(group.groups, tab);
    if (rows.length === 0 && children.length === 0) continue;
    kept.push({ ...group, rows, groups: children });
  }
  return kept;
}

export function MappingTabs({
  active,
  onSelect,
}: {
  active: MappingTabId;
  onSelect(tab: MappingTabId): void;
}) {
  return (
    <div className={styles.tabs} role="tablist" aria-label="Field filter">
      {MAPPING_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={`${styles.tab}${tab.id === active ? ` ${styles.tabOn}` : ""}`}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}
